import type { ChatContext, ChatOperationContract } from '@/store/chat-types'
import type { ChannelDispatchStatus } from '@/store/ide-v2-slice'
import type { RailItemId } from '@/shell/ide-v2/types'
import type { StudioCapabilityExecution, StudioPendingCapability } from './studio-capability-gateway'
import { listStudioChatOperationContracts } from './studio-chat-operation-registry'
import { STUDIO_MODULE_CONTRACTS } from './studio-operation-v2'
import type { StudioProjectOutputKind } from '@/features/project/lib/studio-project-plan'
import {
  findStudioAuthorityTask,
  studioAuthorityNamespace,
  type StudioAuthorityDomain,
} from '@/shell/ide-v2/studio-authority-workspaces'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { COOGO_IDE_CHROME_COPY } from '@/shell/ide-v2/coogo-ide-chrome-copy'
import { getStudioExecutionCopy } from '@/i18n/studio-execution-copy'
import { getStudioAuthorityTaskCopy } from '@/i18n/studio-authority-copy'
import { resolveStudioOperationLabel } from './studio-operation-display-copy'

export type StudioTaskPhase =
  | "understanding"
  | "needs-input"
  | "ready"
  | "awaiting-confirmation"
  | "running"
  | "applied"
  | "verified"
  | "blocked"
  | "failed"
  | "undone";

export interface StudioTaskSnapshot {
  taskId: string;
  title: string;
  phase: StudioTaskPhase;
  phaseLabel: string;
  surface: RailItemId;
  surfaceLabel: string;
  authorityDomain: StudioAuthorityDomain | null;
  authorityView: string | null;
  authorityNamespace: string | null;
  moduleLabel: string;
  targetLabel: string | null;
  message: string;
  nextAction: string;
  operation: ChatOperationContract | null;
  pending: StudioPendingCapability | null;
  execution: StudioCapabilityExecution | null;
}

export interface StudioSurfaceGuidance {
  surface: RailItemId;
  title: string;
  message: string;
  operationExamples: string[];
}

const ACTIVE_DISPATCH_PHASES = new Set<ChannelDispatchStatus["phase"]>([
  "request-in-flight",
  "pattern-retrieval",
  "mock-generation",
  "real-generation",
  "rendering",
]);

const SURFACE_GUIDANCE: Record<RailItemId, string> = {
  preview:
    "Describe the result you want. Coogo will inspect the active page and keep the preview in sync.",
  source:
    "Select a source decision or name the source in chat. Missing source coverage stays blocked instead of being invented.",
  editor:
    "Select a page, block, or element, then describe the exact result you want on that target.",
  behavior:
    "Select an event or ActionFlow. Coogo can review it immediately and prepares edits for confirmation.",
  data: "Select a component or data source. Credentials and unresolved endpoints remain explicit blockers.",
  library:
    "Select a model relationship or coverage issue to attach it to the conversation.",
  code: "Name the Coogo JSON area to inspect. Schema and runtime details stay read-only until a bounded edit is confirmed.",
  map: "Select a page relationship to inspect or navigate to its owning editor surface.",
  evidence:
    "Name the claim to verify. Coogo separates validation, artifact proof, deployment, and readback.",
  report:
    "Select a report section or export target. Native readiness is claimed only after readback proof.",
  document:
    "Select a document page or node. Coogo proposes edits first and preserves page order, revisions, and export blockers.",
  publish:
    "Review release readiness. Publishing always waits for explicit confirmation.",
  deploy:
    "Choose the release target and verify readiness before starting an external deployment.",
  settings:
    "Name the project setting to change. Sensitive values are never echoed into the conversation.",
};

function primarySurfaceGuidance(outputKind?: StudioProjectOutputKind): string {
  if (outputKind === "document") {
    return "Describe the document result you want. Coogo will inspect the current document and keep the canvas in sync.";
  }
  if (outputKind === "game") {
    return "Describe the game result you want. Coogo will inspect the current game and keep the preview in sync.";
  }
  return SURFACE_GUIDANCE.preview;
}

function surfaceLabel(surface: RailItemId, locale: LocaleCode = 'en'): string {
  return COOGO_IDE_CHROME_COPY[locale].surfaceLabels[surface]
}

function targetLabel(context: Partial<ChatContext>, locale: LocaleCode = 'en'): string | null {
  if (context.selectedElementLabel) return context.selectedElementLabel
  if (context.selectedBlockLabel) return context.selectedBlockLabel
  if (context.selectedPropertyKey) return context.selectedPropertyKey
  if (context.pageName) return context.pageName
  if (context.flowName) return context.flowName
  if (context.dataSourceName) return context.dataSourceName
  if (context.documentPageIndex !== undefined) {
    const page = context.documentPageIndex + 1
    if (locale === 'ko') return `문서 ${page}페이지`
    if (locale === 'ja') return `ドキュメント ${page}ページ`
    if (locale === 'zh') return `文档第 ${page} 页`
    if (locale === 'zh-TW') return `文件第 ${page} 頁`
    return `Document page ${page}`
  }
  if (context.documentTarget) return context.documentTarget.toUpperCase()
  if (context.sourceNodeId) {
    if (locale === 'ko') return '선택한 소스 노드'
    if (locale === 'ja') return '選択したソースノード'
    if (locale === 'zh') return '已选源节点'
    if (locale === 'zh-TW') return '已選來源節點'
    return 'Selected source node'
  }
  if (context.sourceWorkspaceId) {
    if (locale === 'ko') return 'Figma 소스 작업 공간'
    if (locale === 'ja') return 'Figma ソースワークスペース'
    if (locale === 'zh') return 'Figma 源工作区'
    if (locale === 'zh-TW') return 'Figma 來源工作區'
    return 'Figma source workspace'
  }
  return null
}

function matchesOperationPlan(
  planId: string,
  operation: ChatOperationContract | null,
): boolean {
  return operation ? planId.startsWith(`operation:${operation.id}:`) : true;
}

function latestMatchingExecution(
  executions: StudioCapabilityExecution[],
  operation: ChatOperationContract | null,
  requestedAt?: number,
): StudioCapabilityExecution | null {
  return (
    [...executions]
      .reverse()
      .find(
        (entry) =>
          matchesOperationPlan(entry.planId, operation) &&
          (!requestedAt || Date.parse(entry.createdAt) >= requestedAt),
      ) ?? null
  );
}

function latestMatchingPending(
  pending: StudioPendingCapability[],
  operation: ChatOperationContract | null,
  requestedAt?: number,
): StudioPendingCapability | null {
  return (
    [...pending]
      .reverse()
      .find(
        (entry) =>
          matchesOperationPlan(entry.planId, operation) &&
          (!requestedAt || Date.parse(entry.createdAt) >= requestedAt),
      ) ?? null
  );
}

export function hasSuccessfulReadback(
  execution: StudioCapabilityExecution,
): boolean {
  const successfulResults = (execution.results ?? []).filter(
    (result) => result.success,
  )
  return (
    successfulResults.length > 0 &&
    successfulResults.every(
      (result) => result.readback?.status === "success",
    )
  )
}

export function buildStudioTaskSnapshot(input: {
  activeSurface: RailItemId
  activeSurfaceLabel?: string
  outputKind?: StudioProjectOutputKind
  context: Partial<ChatContext>
  dispatchStatus: ChannelDispatchStatus
  pending: StudioPendingCapability[]
  executions: StudioCapabilityExecution[]
  /**
   * A proposal is drawn on screen and is waiting for a human decision.
   *
   * `pending` above is the CAPABILITY gateway's queue. It is not the only way
   * this Studio asks for a decision: a generated application arrives as an
   * intent preview with 승인 / 거절 beside it and never touches that queue, so
   * a surface that read only `pending` said "요청 이해 중" while the decision it
   * was waiting for was already on screen. Derived by the caller from the
   * thing the user is looking at (`useIntentPreview`), not re-decided here.
   */
  awaitingDecision?: boolean
  locale?: LocaleCode
}): StudioTaskSnapshot {
  const locale = input.locale ?? 'en'
  const operation = input.context.operationContract ?? null
  const surface = operation?.surface.railItemId ?? input.activeSurface
  const pending = latestMatchingPending(input.pending, operation, input.context.operationRequestedAt)
  const execution = latestMatchingExecution(input.executions, operation, input.context.operationRequestedAt)
  const resolvedSurfaceLabel =
    surface === input.activeSurface && input.activeSurfaceLabel
      ? input.activeSurfaceLabel
      : surfaceLabel(surface, locale)
  const authorityDomain = input.context.authorityDomain ?? null
  const authorityTask = authorityDomain
    ? findStudioAuthorityTask(
        authorityDomain,
        input.context.authorityView,
        input.outputKind ?? input.context.primaryKind ?? "app",
      )
    : null
  const target = targetLabel(input.context, locale)
  // The operator-facing half of the operation label split. `operation.label`
  // stays English because it is serialized into the prompt; what the task
  // centre renders is resolved from the operation id.
  //
  // RESIDUE, deliberate: `pending.label` and `execution.label` below still
  // render English. They come from `StudioPendingCapability` /
  // `StudioCapabilityExecution`, which carry a `planId` but no operation id, so
  // there is no key to resolve them by. Threading one through the capability
  // gateway is a separate change, not a translation.
  const operationDisplayLabel = operation
    ? resolveStudioOperationLabel(operation.id, locale, operation.label)
    : null
  let phase: StudioTaskPhase = 'understanding'
  let message = authorityTask
    ? authorityTask.description
    : surface === "preview"
      ? primarySurfaceGuidance(input.outputKind)
      : SURFACE_GUIDANCE[surface];
  let nextAction =
    authorityDomain === "insights"
      ? "Review the evidence or open Chat to propose a confirmed handoff."
      : "Describe the result you want in chat.";

  if (operation?.availability === "blocked") {
    phase = "blocked";
    message =
      operation.guidance ?? `${operationDisplayLabel} is not available yet.`;
    nextAction = "Review the blocker and provide the missing source or input.";
  } else if (pending) {
    phase = "awaiting-confirmation";
    message = `${pending.label} is prepared. Nothing changes until you confirm.`;
    nextAction = "Review the proposed change, then confirm or cancel.";
  } else if (ACTIVE_DISPATCH_PHASES.has(input.dispatchStatus.phase)) {
    phase = "running";
    message =
      input.dispatchStatus.message ?? "Coogo is working on the request.";
    nextAction = "Keep this task open while Coogo completes its readback.";
  } else if (input.awaitingDecision) {
    phase = "awaiting-confirmation";
    message = "A generated result is on screen. Nothing changes until you decide.";
    nextAction = "Review the result, then approve or reject it.";
  } else if (execution) {
    phase =
      execution.status === "failed"
        ? "failed"
        : execution.status === "cancelled"
          ? "blocked"
          : execution.status === "running"
            ? "running"
            : execution.status === "suspended"
              ? "awaiting-confirmation"
              : execution.status === "undone"
          ? "undone"
          : hasSuccessfulReadback(execution)
            ? "verified"
            : "applied";
    message =
      execution.status === "failed"
        ? (execution.error ?? `${execution.label} could not be completed.`)
        : execution.status === "cancelled"
          ? `${execution.label} was cancelled before completion.`
          : execution.status === "running"
            ? `${execution.label} is still running according to the durable journal.`
            : execution.status === "suspended"
              ? `${execution.label} is suspended and needs an explicit continuation decision.`
              : execution.status === "undone"
          ? `${execution.label} was undone.`
          : hasSuccessfulReadback(execution)
            ? `${execution.label} was verified by readback.`
            : `${execution.label} was applied. Review the current Studio result.`;
    nextAction =
      execution.status === "executed"
        ? execution.undoAvailable
          ? "Continue in chat or undo this change."
          : "Continue in chat or describe another change."
        : execution.status === "running" || execution.status === "suspended"
          ? "Review the durable timeline before continuing this run."
          : "Adjust the request or choose another target.";
  } else if (
    operation &&
    operation.targetKinds.length > 0 &&
    !input.context.operationContextFrame?.target
  ) {
    phase = "needs-input";
    message = `${operationDisplayLabel} needs a specific ${operation.targetKinds.join(" or ")} target.`;
    nextAction = "Select the target in Studio or name it in chat.";
  } else if (operation) {
    phase = "ready";
    message = operation.readOnly
      ? `${operationDisplayLabel} can run now without changing the workspace.`
      : `${operationDisplayLabel} can be prepared now. Studio will ask before applying it.`;
    nextAction = operation.readOnly
      ? "Send the review request."
      : "Describe the intended change and review the confirmation.";
  } else if (input.dispatchStatus.phase === "failed") {
    phase = "failed";
    message =
      input.dispatchStatus.message ?? "The last request needs attention.";
    nextAction = "Review the error and clarify the request.";
  } else if (input.dispatchStatus.phase === "completed") {
    // A finished channel dispatch had NO branch here, so the surface fell back
    // to `understanding` the moment the work it had been reporting succeeded —
    // the same stale sentence, now after the fact. `applied` is the honest
    // word for it: `useChannelDispatch` writes `completed` only after
    // `applyRenderedOutputToProject` has replaced the project's blocks.
    phase = "applied";
    message =
      input.dispatchStatus.message ?? "The generated result was applied.";
    nextAction = "Continue in chat or describe another change.";
  }

  return {
    taskId:
      operation?.id ??
      (authorityDomain && authorityTask
        ? `${studioAuthorityNamespace(authorityDomain)}:${authorityTask.id}`
        : `workspace:${surface}`),
    title:
      operationDisplayLabel ??
      (authorityTask
        ? resolvedSurfaceLabel.endsWith(`· ${authorityTask.label}`)
          ? resolvedSurfaceLabel
          : `${resolvedSurfaceLabel} · ${authorityTask.label}`
        : `${resolvedSurfaceLabel} task`),
    phase,
    phaseLabel: getStudioExecutionCopy(locale).commandDeck.phase[phase],
    surface,
    surfaceLabel: resolvedSurfaceLabel,
    authorityDomain,
    authorityView: authorityTask?.id ?? null,
    authorityNamespace: authorityDomain
      ? studioAuthorityNamespace(authorityDomain)
      : null,
    moduleLabel: operation
      ? STUDIO_MODULE_CONTRACTS[operation.moduleId].label
      : resolvedSurfaceLabel,
    targetLabel: target,
    message,
    nextAction,
    operation,
    pending,
    execution,
  };
}

export function getStudioSurfaceGuidance(
  surface: RailItemId,
): StudioSurfaceGuidance {
  const operations = listStudioChatOperationContracts()
    .filter(
      (operation) =>
        operation.surface.railItemId === surface &&
        operation.availability !== "blocked",
    )
    .slice(0, 3);

  return {
    surface,
    title: `${surfaceLabel(surface)} is ready`,
    message: SURFACE_GUIDANCE[surface],
    operationExamples: operations.map(
      (operation) => operation.examples[0] ?? operation.description,
    ),
  };
}

/**
 * The chat input's placeholder.
 *
 * `hasSourceMarkup` (task-4 fix-round-4) is the As-Is source-truth-vs-derived
 * answer, and it must come from `useAsIsSourceMarkupPresent`
 * (features/preview/hooks/use-as-is-source-markup.ts) — the one decision point
 * every As-Is provenance surface shares — never from a second store read here.
 * Without it this placeholder said "Describe what you need in original
 * structure" for a snapshot whose ContextBadge, directly above the input, said
 * "Original": the app contradicted itself inside a single composer.
 *
 * The default `false` keeps every caller that does not pass it on today's
 * wording, so no non-As-Is surface moves.
 */
export function getStudioComposerPlaceholder(
  surface: RailItemId,
  outputKind?: StudioProjectOutputKind,
  context?: Partial<ChatContext>,
  locale: LocaleCode = 'en',
  hasSourceMarkup = false,
): string {
  const composerCopy = COOGO_IDE_CHROME_COPY[locale].composer
  if (context?.authorityDomain && context.authorityView) {
    const task = findStudioAuthorityTask(
      context.authorityDomain,
      context.authorityView,
      outputKind ?? context.primaryKind ?? 'app',
    )
    const taskCopy = getStudioAuthorityTaskCopy(
      locale,
      context.authorityDomain,
      outputKind ?? context.primaryKind ?? 'app',
      task,
      hasSourceMarkup,
    )
    if (context.authorityDomain === 'insights') {
      return composerCopy.askToReview(taskCopy.label)
    }
    return composerCopy.askAbout(taskCopy.label)
  }
  if (surface === 'preview' && outputKind === 'document') {
    return composerCopy.documentPreview
  }
  if (surface === 'preview' && outputKind === 'game') {
    return composerCopy.gamePreview
  }
  return composerCopy[surface]
}
